import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Divider,
  message,
} from 'antd';
import {
  TeamOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const PERMISSIONS = [
  { key: 'clients.read', label: 'Clients: Read' },
  { key: 'clients.write', label: 'Clients: Write' },
  { key: 'campaigns.read', label: 'Campaigns: Read' },
  { key: 'campaigns.write', label: 'Campaigns: Write' },
  { key: 'billing.read', label: 'Billing: Read' },
  { key: 'billing.manage', label: 'Billing: Manage' },
  { key: 'analytics.view', label: 'Analytics: View' },
  { key: 'integrations.manage', label: 'Integrations: Manage' },
  { key: 'settings.manage', label: 'Settings: Manage' },
];

const WhiteLabelRoles = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const roles = useMemo(() => ([
    { key: 'admin', name: 'Admin', users: 4, active: true, permissions: ['clients.read','clients.write','campaigns.read','campaigns.write','billing.manage','settings.manage'] },
    { key: 'manager', name: 'Manager', users: 7, active: true, permissions: ['clients.read','campaigns.read','campaigns.write','analytics.view'] },
    { key: 'analyst', name: 'Analyst', users: 3, active: false, permissions: ['analytics.view'] },
    { key: 'billing', name: 'Billing', users: 2, active: true, permissions: ['billing.read','billing.manage'] },
  ]), []);

  const openDrawer = (record) => {
    setSelected(record || null);
    form.setFieldsValue(record || { name: '', description: '', active: true, permissions: [] });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // TODO: POST/PUT to backend
      void values;
      message.success(selected ? 'Role updated' : 'Role created');
      setDrawerOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Role', dataIndex: 'name', key: 'name' },
    { title: 'Users', dataIndex: 'users', key: 'users', width: 100 },
    { title: 'Permissions', dataIndex: 'permissions', key: 'permissions', render: (perms) => (
      <Space wrap>
        {perms.slice(0, 4).map(p => {
          const meta = PERMISSIONS.find(m => m.key === p);
          return <Tag key={p}>{meta ? meta.label : p}</Tag>;
        })}
        {perms.length > 4 && <Tag>+{perms.length - 4} more</Tag>}
      </Space>
    ) },
    { title: 'Status', dataIndex: 'active', key: 'active', width: 120, render: (a) => (
      <Tag color={a ? 'green' : 'default'}>{a ? 'Active' : 'Disabled'}</Tag>
    ) },
    { title: 'Actions', key: 'actions', width: 220, render: (_, r) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => openDrawer(r)}>Edit</Button>
        <Button danger icon={<DeleteOutlined />}>Delete</Button>
      </Space>
    ) },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Role Permissions</Title>
          <Text type="secondary">Assign permissions within the agency. Scope is limited to agency and its clients.</Text>
        </div>

        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={12}>
              <Space>
                <TeamOutlined />
                <Text type="secondary">Manage roles and permissions for your team</Text>
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>New Role</Button>
            </Col>
          </Row>
        </Card>

        <Card title="Roles" bodyStyle={{ padding: 0 }}>
          <Table rowKey="key" columns={columns} dataSource={roles} pagination={{ pageSize: 8 }} />
        </Card>

        {/* Create/Edit Drawer */}
        <Drawer
          title={selected ? `Edit Role: ${selected.name}` : 'Create Role'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={560}
          destroyOnClose
        >
          <Form layout="vertical" form={form}>
            <Form.Item label="Role Name" name="name" rules={[{ required: true, message: 'Enter role name' }]}>
              <Input placeholder="Manager" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <Input.TextArea placeholder="Describe this role" rows={3} />
            </Form.Item>
            <Form.Item label="Active" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Divider style={{ margin: '8px 0' }} />
            <Form.Item label="Permissions" name="permissions" rules={[{ required: true, message: 'Select at least one permission' }]}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Select permissions"
                options={PERMISSIONS.map(p => ({ value: p.key, label: p.label }))}
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
              </Space>
            </div>
          </Form>
        </Drawer>
      </Space>
    </div>
  );
};

export default WhiteLabelRoles;
